/**
 * FundingFinder Component
 * Displays funding opportunities with partnership search
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Row, Col, Spinner, Alert, Form, Badge } from 'react-bootstrap';

import { useOpportunityApi } from '@/shared/services/opportunityApi';
import useEcosystemTaxonomy from '@/shared/hooks/useEcosystemTaxonomy';
import useCapability from '@/shared/hooks/useCapability';

import '../Opportunities.css';

const FUNDING_TYPES = [
    { value: 'grant', label: 'Grant' },
    { value: 'contract', label: 'Contract' },
    { value: 'sponsorship', label: 'Sponsorship' },
    { value: 'donation', label: 'Donation' },
    { value: 'matching', label: 'Matching Funds' },
    { value: 'seed', label: 'Seed Funding' },
    { value: 'prize', label: 'Prize/Award' },
];

function formatAmount(amount) {
    if (!amount) return null;
    const { min, max } = amount;

    if (min && max) {
        if (min === max) {
            return `$${min.toLocaleString()}`;
        }
        return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    }
    if (max) {
        return `Up to $${max.toLocaleString()}`;
    }
    if (min) {
        return `From $${min.toLocaleString()}`;
    }
    return null;
}

function formatDeadline(dateStr) {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Deadline passed';
    if (diffDays === 0) return 'Due today!';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays <= 7) return `${diffDays} days left`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} weeks left`;

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

export default function FundingFinder() {
    const opportunityApi = useOpportunityApi();
    // Tenant-scoped sector taxonomy: replaces the hardcoded four-org-type
    // dropdown that was previously gated behind an unrelated
    // `filterOptions.regions` guard (the gate had nothing to do with org
    // types and would hide the entire control if regions failed to load).
    // The `filterOptions` prop is no longer read: the parent
    // (OpportunitiesPage) still passes it for API symmetry with peer
    // tabs, so we drop the parameter rather than keep an unused one.
    const { sectors: tenantSectors } = useEcosystemTaxonomy();
    // Opportunity partnerships ("seeking partners") are a per-tenant
    // capability. When the active ecosystem has disabled
    // `opportunityPartnerships`, hide the Seeking Partners filter, the
    // partnership CTA, and the per-card seeking-partners badge.
    const partnershipsEnabled = useCapability('opportunityPartnerships');

    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [fundingType, setFundingType] = useState('');
    const [seekingPartners, setSeekingPartners] = useState(false);
    const [eligibleOrgType, setEligibleOrgType] = useState('');
    const [minAmount, setMinAmount] = useState('');

    useEffect(() => {
        const loadFunding = async () => {
            setLoading(true);
            setError(null);

            try {
                const params = {};
                if (fundingType) params.fundingType = fundingType;
                if (seekingPartners) params.seekingPartners = 'true';
                if (eligibleOrgType) params.eligibleOrgType = eligibleOrgType;
                if (minAmount) params.minAmount = minAmount;

                const result = await opportunityApi.fetchFunding(params);
                setOpportunities(result.opportunities || []);
            } catch (err) {
                console.error('Failed to load funding opportunities:', err);
                setError(
                    'Failed to load funding opportunities. Please try again.'
                );
            } finally {
                setLoading(false);
            }
        };

        loadFunding();
    }, [
        opportunityApi,
        fundingType,
        seekingPartners,
        eligibleOrgType,
        minAmount,
    ]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">
                    Loading funding opportunities...
                </p>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div className="funding-finder">
            {/* Filters */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small text-muted">
                                    Funding Type
                                </Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={fundingType}
                                    onChange={(e) =>
                                        setFundingType(e.target.value)
                                    }
                                >
                                    <option value="">All Types</option>
                                    {FUNDING_TYPES.map((type) => (
                                        <option
                                            key={type.value}
                                            value={type.value}
                                        >
                                            {type.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small text-muted">
                                    Eligible Org Type
                                </Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={eligibleOrgType}
                                    onChange={(e) =>
                                        setEligibleOrgType(e.target.value)
                                    }
                                >
                                    <option value="">Any Organization</option>
                                    {tenantSectors.map((sector) => (
                                        <option key={sector} value={sector}>
                                            {sector}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small text-muted">
                                    Min Amount
                                </Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={minAmount}
                                    onChange={(e) =>
                                        setMinAmount(e.target.value)
                                    }
                                >
                                    <option value="">No minimum</option>
                                    <option value="1000">$1,000+</option>
                                    <option value="5000">$5,000+</option>
                                    <option value="10000">$10,000+</option>
                                    <option value="25000">$25,000+</option>
                                    <option value="50000">$50,000+</option>
                                    <option value="100000">$100,000+</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        {partnershipsEnabled && (
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="small text-muted">
                                        &nbsp;
                                    </Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id="seeking-partners"
                                        label="Seeking Partners"
                                        checked={seekingPartners}
                                        onChange={(e) =>
                                            setSeekingPartners(e.target.checked)
                                        }
                                        className="mt-2"
                                    />
                                </Form.Group>
                            </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>

            {/* Partnership CTA */}
            {partnershipsEnabled && seekingPartners && (
                <Alert variant="info" className="mb-4">
                    <strong>Looking for collaboration?</strong> The
                    opportunities below are actively seeking partners. Reach out
                    to discuss potential collaboration!
                </Alert>
            )}

            {/* Results */}
            {opportunities.length === 0 ? (
                <div className="text-center py-5">
                    <div className="display-1 mb-3">💰</div>
                    <h4>No funding opportunities found</h4>
                    <p className="text-muted">
                        Try adjusting your filters or check back later.
                    </p>
                </div>
            ) : (
                <Row className="g-4">
                    {opportunities.map((opp) => (
                        <Col key={opp._id} md={6}>
                            <Card
                                as={Link}
                                to={`/opportunities/${opp._id}`}
                                className={`h-100 border-0 shadow-sm funding-card text-decoration-none ${partnershipsEnabled && opp.funding?.seekingPartners ? 'seeking-partners' : ''}`}
                            >
                                <Card.Body>
                                    {opp.funding?.amount && (
                                        <div className="funding-amount mb-2">
                                            {formatAmount(opp.funding.amount)}
                                        </div>
                                    )}
                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                        <Badge bg="success">
                                            {opp.funding?.fundingType ||
                                                opp.type}
                                        </Badge>
                                        {partnershipsEnabled &&
                                            opp.funding?.seekingPartners && (
                                                <Badge
                                                    bg="info"
                                                    className="partner-badge"
                                                >
                                                    🤝 Seeking Partners
                                                </Badge>
                                            )}
                                    </div>

                                    <h5 className="text-primary mb-2">
                                        {opp.title}
                                    </h5>

                                    <p className="text-muted small mb-2">
                                        {opp.organization?.name}
                                    </p>

                                    {opp.funding?.funder && (
                                        <p className="small mb-2">
                                            <strong>Funder:</strong>{' '}
                                            {opp.funding.funder}
                                        </p>
                                    )}

                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                        {opp.application?.deadline && (
                                            <Badge
                                                bg={
                                                    new Date(
                                                        opp.application.deadline
                                                    ) < new Date()
                                                        ? 'secondary'
                                                        : 'warning'
                                                }
                                                text="dark"
                                            >
                                                ⏰{' '}
                                                {formatDeadline(
                                                    opp.application.deadline
                                                )}
                                            </Badge>
                                        )}
                                        {opp.application?.isRolling && (
                                            <Badge bg="light" text="dark">
                                                Rolling Deadline
                                            </Badge>
                                        )}
                                        {opp.funding?.matchRequired && (
                                            <Badge bg="light" text="dark">
                                                Match Required
                                            </Badge>
                                        )}
                                    </div>

                                    {opp.funding?.eligibleActivities &&
                                        opp.funding.eligibleActivities.length >
                                            0 && (
                                            <p className="small text-muted mb-0">
                                                <strong>For:</strong>{' '}
                                                {opp.funding.eligibleActivities
                                                    .slice(0, 3)
                                                    .join(', ')}
                                                {opp.funding.eligibleActivities
                                                    .length > 3 && '...'}
                                            </p>
                                        )}

                                    {partnershipsEnabled &&
                                        opp.funding?.seekingPartners &&
                                        opp.funding?.partnerRequirements && (
                                            <div className="mt-3 p-2 bg-light rounded">
                                                <small className="text-muted">
                                                    <strong>
                                                        Looking for partners:
                                                    </strong>{' '}
                                                    {opp.funding.partnerRequirements.substring(
                                                        0,
                                                        150
                                                    )}
                                                    {opp.funding
                                                        .partnerRequirements
                                                        .length > 150 && '...'}
                                                </small>
                                            </div>
                                        )}
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </div>
    );
}
